import type { SxProps, Theme } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';

/** Matches brand audition manage filter controls (`ManageAuditionContainer`). */
export const BRAND_CONTEST_FILTER_MIN_HEIGHT = 52;

export const midnight = {
    primary: BRAND_LUXE_PRIMARY,
    secondary: BRAND_LUXE_SECONDARY,
    surface: '#0e0e0e',
    surfaceLow: BRAND_SURFACE_CONTAINER_LOW,
    surfaceContainer: BRAND_SURFACE_CONTAINER,
    surfaceContainerHigh: BRAND_SURFACE_CONTAINER_HIGH,
    surfaceHighest: BRAND_SURFACE_CONTAINER_HIGHEST,
    onSurface: '#ffffff',
    onSurfaceVariant: '#adaaaa',
    outline: BRAND_OUTLINE_VARIANT,
};

export const editorialHeadlineSx: SxProps<Theme> = {
    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    fontWeight: 900,
    letterSpacing: '-0.04em',
};

/** Flush-left rich HTML — resets Quill `.ql-editor` padding so copy aligns with section labels. */
export const brandContestRichTextViewerSx: SxProps<Theme> = {
    width: '100%',
    '& .rich-text-wrapper': {
        width: '100%',
    },
    '& .rich-text-wrapper .ql-editor': {
        padding: '0 !important',
        margin: 0,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        lineHeight: 1.7,
        fontSize: 'inherit',
        '& > div': {
            padding: 0,
            margin: 0,
        },
        '& p': {
            margin: '0 0 0.65em',
            paddingLeft: 0,
            marginLeft: 0,
            '&:last-child': { marginBottom: 0 },
        },
        '& strong': {
            fontWeight: 700,
            color: midnight.onSurface,
        },
    },
};

export const editorialLabelSx: SxProps<Theme> = {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    color: midnight.onSurfaceVariant,
};

export const editorialSelectMenuProps = {
    PaperProps: {
        sx: {
            bgcolor: midnight.surfaceHighest,
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.08)',
            mt: 0.5,
            '& .MuiMenuItem-root': {
                color: midnight.onSurface,
                fontSize: 14,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                '&.Mui-selected': {
                    bgcolor: 'rgba(255,201,101,0.15)',
                    color: midnight.onSurface,
                    '&:hover': { bgcolor: 'rgba(255,201,101,0.22)' },
                },
            },
        },
    },
};

/** Pass as `SelectProps` on MUI `TextField select` for themed menu + chevron. */
export const editorialSelectProps = {
    MenuProps: editorialSelectMenuProps,
};

export const editorialInputSx: SxProps<Theme> = {
    '& .MuiInputLabel-root': {
        color: midnight.onSurface,
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: midnight.onSurface,
    },
    '& .MuiInputLabel-root.Mui-disabled': {
        color: midnight.onSurfaceVariant,
    },
    '& .MuiOutlinedInput-root': {
        bgcolor: midnight.surfaceHighest,
        borderRadius: '12px',
        color: midnight.onSurface,
        '& fieldset': { border: 'none' },
        '&:hover fieldset': { border: 'none' },
        '&.Mui-focused fieldset': { border: 'none' },
        '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(255,201,101,0.35)' },
    },
    '& .MuiInputBase-input': { color: midnight.onSurface },
    '& .MuiInputBase-input::placeholder': { color: midnight.onSurfaceVariant, opacity: 1 },
    '& .MuiSelect-select': { color: midnight.onSurface },
    '& .MuiSelect-icon': { color: midnight.onSurface },
    '& .MuiSelect-iconOpen': { color: midnight.primary },
};

export const selectionCardSx = (selected: boolean): SxProps<Theme> => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    p: 4,
    borderRadius: '24px',
    bgcolor: selected ? midnight.surfaceContainer : midnight.surfaceLow,
    border: '1px solid',
    borderColor: selected ? 'rgba(255,201,101,0.45)' : 'transparent',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    '&:hover': {
        borderColor: 'rgba(255,201,101,0.35)',
        bgcolor: midnight.surfaceContainer,
        transform: 'translateY(-2px)',
    },
});

export const contestWizardDatePickerSx: SxProps<Theme> = {
    border: 'none',
    borderRadius: '12px',
    backgroundColor: midnight.surfaceHighest,
    '&:focus-within': {
        border: 'none',
        boxShadow: '0 0 0 1px rgba(255,201,101,0.35)',
    },
};

export const glowMintBarSx: SxProps<Theme> = {
    height: '100%',
    borderRadius: 999,
    background: midnight.secondary,
    boxShadow: '0 0 15px -3px rgba(0, 253, 193, 0.35)',
    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
};

export const brandContestFilterSearchSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
        minHeight: BRAND_CONTEST_FILTER_MIN_HEIGHT,
        borderRadius: 9999,
        bgcolor: midnight.surfaceHighest,
        px: 1.5,
        transition: 'box-shadow 0.2s ease',
        '& fieldset': { border: 'none' },
        '&:hover fieldset': { border: 'none' },
        '&.Mui-focused fieldset': { border: `1px solid ${midnight.primary}` },
        '& .MuiInputBase-input': {
            color: midnight.onSurface,
            fontSize: '0.9375rem',
            py: 1.25,
            pl: 0.5,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        '& .MuiInputBase-input::placeholder': {
            color: midnight.onSurfaceVariant,
            opacity: 1,
        },
    },
};

/**
 * ≤1024px only (mobile + 1024×1366 / 1024×600). Wider viewports keep existing filter/pagination layout.
 */
export const BRAND_CONTEST_COMPACT_MAX_WIDTH_PX = 1024;

/** Category + sort row — stacked full-width under search when viewport ≤1024px */
export const brandContestFilterFieldsRowSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    width: '100%',
    '@media (min-width: 1025px) and (max-width: 1199.95px)': {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    '@media (min-width: 1200px)': {
        flexDirection: 'row',
        flexWrap: 'nowrap',
        width: 'auto',
        justifyContent: 'flex-end',
    },
};

export const brandContestFilterControlSx: SxProps<Theme> = {
    width: '100%',
    m: 0,
    minWidth: 0,
    flex: '1 1 100%',
    '& .MuiInputBase-root': {
        width: '100%',
    },
    '@media (min-width: 1025px)': {
        width: 'auto',
        flex: '0 1 auto',
        '& .MuiInputBase-root': {
            width: 'auto',
        },
    },
};

/** Brand pagination shell — wraps on ≤1024px so range text is not clipped */
export const brandContestPaginationCompactShellSx: SxProps<Theme> = {
    '@media (max-width: 1024px)': {
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 1.5,
        px: 1.5,
        py: 2,
    },
};

export const brandContestPaginationTableSx: SxProps<Theme> = {
    '@media (max-width: 1024px)': {
        width: '100%',
        maxWidth: '100%',
        '& .MuiToolbar-root': {
            width: '100%',
            maxWidth: '100%',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1,
            rowGap: 1.25,
        },
        '& .MuiTablePagination-selectLabel': {
            fontSize: '0.58rem',
            letterSpacing: '0.08em',
        },
        '& .MuiTablePagination-displayedRows': {
            fontSize: '0.6rem',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            overflow: 'visible',
        },
        '& .MuiTablePagination-input': {
            fontSize: '0.65rem',
        },
    },
};

export const brandContestFilterSelectSx: SxProps<Theme> = {
    minHeight: BRAND_CONTEST_FILTER_MIN_HEIGHT,
    borderRadius: 9999,
    bgcolor: midnight.surfaceHighest,
    color: midnight.onSurfaceVariant,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    fontFamily: FONT_ENUM.MAIN_BOLD,
    '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        minHeight: BRAND_CONTEST_FILTER_MIN_HEIGHT,
        py: 0,
        px: 2.5,
        pr: '36px !important',
        boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `1px solid ${midnight.primary}` },
    '& .MuiSelect-icon': { color: midnight.onSurface, opacity: 0.9, right: 10 },
};

/** Same label styling as `brandContestFilterSelectSx` (pill filters). */
export const brandContestTableHeaderText = {
    color: midnight.onSurfaceVariant,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
} as const;

export const brandContestTableHeaderTextSx: SxProps<Theme> = brandContestTableHeaderText;

/** Body copy: filter family, readable size (not uppercase). */
export const brandContestTableCellTextSx: SxProps<Theme> = {
    color: midnight.onSurfaceVariant,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'none',
};

/** Juror search field on brand contest manage → editorial board. */
export const brandJurorSearchFieldSx: SxProps<Theme> = {
    flex: 1,
    position: 'relative',
    bgcolor: midnight.surfaceHighest,
    borderRadius: '9999px',
    minHeight: BRAND_CONTEST_FILTER_MIN_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    px: 1.5,
    py: 1,
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
        boxShadow: '0 0 0 1px rgba(255,201,101,0.25)',
    },
    '&:focus-within': {
        boxShadow: `0 0 0 1px ${midnight.primary}`,
    },
    '& input::placeholder': {
        color: midnight.onSurfaceVariant,
        opacity: 1,
    },
};

export const brandJurorSearchDropdownSx: SxProps<Theme> = {
    bgcolor: midnight.surfaceHighest,
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
    mt: 1,
    maxHeight: 280,
    overflowY: 'auto',
};

/** Darker than surfaceHighest so white / onSurface text stays readable on hover. */
export const brandJurorSearchDropdownItemHoverBg = midnight.surfaceContainer;

export const brandContestSecondaryIconButtonSx: SxProps<Theme> = {
    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
    color: BRAND_LUXE_PRIMARY,
    border: '1px solid rgba(255,201,101,0.25)',
    '&:hover': { bgcolor: 'rgba(255,201,101,0.12)' },
    '&.Mui-disabled': {
        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
        color: 'rgba(255, 201, 101, 0.42)',
        border: '1px solid rgba(255,201,101,0.18)',
        opacity: 1,
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
        color: 'rgba(255, 201, 101, 0.42)',
    },
};

export const brandContestPrimaryIconButtonSx: SxProps<Theme> = {
    bgcolor: BRAND_LUXE_PRIMARY,
    color: '#392700',
    '&:hover': { bgcolor: BRAND_LUXE_PRIMARY, filter: 'brightness(1.05)' },
};

export const brandContestDangerIconButtonSx: SxProps<Theme> = {
    bgcolor: 'rgba(255, 115, 81, 0.14)',
    color: BRAND_LUXE_PRIMARY,
    border: '1px solid rgba(255, 115, 81, 0.35)',
    '&:hover': { bgcolor: 'rgba(255, 115, 81, 0.22)' },
    '&.Mui-disabled': {
        bgcolor: 'rgba(255,255,255,0.06)',
        color: 'rgba(255,255,255,0.35)',
        border: '1px solid rgba(255,255,255,0.08)',
    },
};

export const brandAwardTitleTextSx: SxProps<Theme> = {
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: midnight.onSurface,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
};

export const brandAwardBodyTextSx: SxProps<Theme> = {
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    fontSize: '0.8125rem',
    color: midnight.onSurfaceVariant,
    lineHeight: 1.45,
};

export const brandAwardAccentTextSx: SxProps<Theme> = {
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    fontSize: '0.75rem',
    color: midnight.primary,
    lineHeight: 1.4,
};

export const brandAwardWarningBannerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    bgcolor: 'rgba(255, 201, 101, 0.08)',
    border: '1px solid rgba(255, 201, 101, 0.28)',
    borderRadius: '12px',
    p: 2,
    mb: 3,
};

export const brandAwardEmptyStateSx: SxProps<Theme> = {
    bgcolor: midnight.surfaceHighest,
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.06)',
    p: 4,
    textAlign: 'center',
};

export const brandAwardCategoryCardSx = (selected = false): SxProps<Theme> => ({
    position: 'relative',
    overflow: 'hidden',
    bgcolor: 'transparent',
    background: 'linear-gradient(145deg, #1c1c1c 0%, #121212 100%)',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: selected ? 'rgba(255, 181, 71, 0.4)' : 'rgba(255, 255, 255, 0.08)',
    boxShadow: selected ? '0 0 40px rgba(255, 181, 71, 0.1)' : 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease',
    boxSizing: 'border-box',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle, rgba(255, 181, 71, 0.03) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    '&:hover': {
        borderColor: 'rgba(255, 181, 71, 0.35)',
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
    },
});

export const brandAwardCardHeadlineSx: SxProps<Theme> = {
    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: midnight.onSurface,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const brandAwardCardTextNoWrapSx: SxProps<Theme> = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const brandAwardTierBadgeSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    mt: 0.5,
    px: 1.25,
    py: 0.35,
    borderRadius: 999,
    bgcolor: 'rgba(255, 181, 71, 0.1)',
    border: '1px solid rgba(255, 181, 71, 0.2)',
    maxWidth: '100%',
};

export const brandAwardTierBadgeTextSx: SxProps<Theme> = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: midnight.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const brandAwardPrizeLabelSx: SxProps<Theme> = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 10,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: midnight.outline,
    mb: 0.5,
    whiteSpace: 'nowrap',
};

export const brandAwardPrizeAmountSx: SxProps<Theme> = {
    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    fontWeight: 800,
    fontSize: { xs: 22, sm: 24, md: 26 },
    lineHeight: 1,
    letterSpacing: '-0.03em',
    color: midnight.primary,
    textShadow: '0 0 16px rgba(255, 181, 71, 0.35), 0 0 32px rgba(255, 181, 71, 0.15)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const brandAwardCardDividerSx: SxProps<Theme> = {
    height: '1px',
    width: '100%',
    my: 2.5,
    background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)',
};

export const brandAwardExtraPrizeRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 1.5,
    borderRadius: '8px',
    bgcolor: midnight.surfaceLow,
    border: '1px solid rgba(255,255,255,0.05)',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        bgcolor: midnight.surfaceContainer,
    },
};

export const brandContestDialogPaperSx: SxProps<Theme> = {
    bgcolor: midnight.surface,
    backgroundImage: 'none',
    borderRadius: { xs: 0, sm: '20px' },
    border: '1px solid rgba(255,255,255,0.08)',
    color: midnight.onSurface,
    maxHeight: { xs: '100%', sm: '92vh' },
    m: { xs: 0, sm: 2 },
};

export const brandContestDialogFooterSx: SxProps<Theme> = {
    borderTop: '1px solid rgba(72,72,71,0.35)',
    px: { xs: 2, md: 3 },
    py: 2.5,
    gap: 1.5,
};

export const brandAwardCheckboxSx: SxProps<Theme> = {
    p: 0,
    color: midnight.outline,
    borderRadius: '4px',
    '& .MuiSvgIcon-root': {
        fontSize: 22,
        borderRadius: '4px',
    },
    '&.Mui-checked': {
        color: midnight.primary,
    },
    '&:hover': {
        bgcolor: 'transparent',
    },
};

export const brandJurorSearchDropdownItemSx: SxProps<Theme> = {
    px: 2,
    py: 1.25,
    cursor: 'pointer',
    '&:hover': {
        bgcolor: brandJurorSearchDropdownItemHoverBg,
    },
};

export const brandContestTableShellSx: SxProps<Theme> = {
    bgcolor: midnight.surfaceLow,
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.06)',
    p: 3,
    overflow: 'visible',
};

/** Subtle lift on dark rows — keeps `onSurfaceVariant` text readable (role_hover is too light on brand fill). */
const brandContestRowHoverBg = 'rgba(255, 255, 255, 0.06)';

/** DataGrid skin — same fill as filter pills (`surfaceHighest`). */
export const brandContestDataGridSx: SxProps<Theme> = {
    minHeight: 450,
    border: 'none',
    borderRadius: '12px',
    bgcolor: midnight.surface,
    overflow: 'visible',
    overflowX: 'auto',
    /** MUI paints header rows with `--DataGrid-containerBackground` (theme paper = white). */
    '--DataGrid-containerBackground': midnight.surface,
    '--DataGrid-pinnedBackground': midnight.surface,
    '--unstable_DataGrid-radius': '12px',
    '& .MuiDataGrid-root': {
        border: 'none',
        bgcolor: midnight.surface,
        borderRadius: '12px',
        '--DataGrid-containerBackground': midnight.surface,
        '--unstable_DataGrid-radius': '12px',
    },
    '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]': {
        background: `${midnight.surface} !important`,
    },
    '& .MuiDataGrid-main': {
        bgcolor: midnight.surface,
        overflow: 'visible',
    },
    '& .MuiDataGrid-columnHeaders': {
        bgcolor: midnight.surface,
        backgroundColor: midnight.surface,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        overflow: 'hidden',
    },
    '& .MuiDataGrid-columnHeader': {
        bgcolor: midnight.surfaceHighest,
        backgroundColor: midnight.surfaceHighest,
        borderRight: '1px solid rgba(255,255,255,0.04)',
        '&:focus': { outline: 'none !important' },
        '&:focus-within': { outline: 'none !important' },
        '&.Mui-focusVisible': { outline: 'none !important' },
        '&:hover': { bgcolor: brandContestRowHoverBg },
    },
    '& .MuiDataGrid-columnHeader[data-field="project.feature_image"]': {
        borderTopLeftRadius: '12px',
    },
    '& .MuiDataGrid-root--noToolbar [aria-rowindex="1"] [aria-colindex="1"]': {
        borderTopLeftRadius: '12px',
    },
    '& .MuiDataGrid-columnHeader--last': {
        borderTopRightRadius: '12px',
    },
    '& .MuiDataGrid-root--noToolbar [aria-rowindex="1"] .MuiDataGrid-columnHeader--last': {
        borderTopRightRadius: '12px',
    },
    '& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller--header': {
        bgcolor: midnight.surfaceHighest,
        backgroundColor: midnight.surfaceHighest,
    },
    '& .MuiDataGrid-virtualScroller': {
        bgcolor: midnight.surfaceHighest,
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        overflow: 'hidden',
    },
    '& .MuiDataGrid-columnHeaderTitle': brandContestTableHeaderTextSx,
    '& .MuiDataGrid-columnHeaderTitleContainerContent': brandContestTableHeaderTextSx,
    '& .MuiDataGrid-cell': {
        borderColor: 'rgba(255,255,255,0.04)',
        bgcolor: midnight.surfaceHighest,
        ...brandContestTableCellTextSx,
        '&:focus': { outline: 'none !important', outlineOffset: 0 },
        '&:focus-within': { outline: 'none !important', outlineOffset: 0 },
    },
    '& .MuiDataGrid-row': { bgcolor: midnight.surfaceHighest },
    '& .MuiDataGrid-row:hover': { bgcolor: brandContestRowHoverBg },
    '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
        bgcolor: brandContestRowHoverBg,
        color: midnight.onSurfaceVariant,
    },
    '& .MuiDataGrid-row:hover .MuiTypography-root': {
        color: midnight.onSurfaceVariant,
    },
    '& .MuiDataGrid-row--firstVisible:hover .MuiDataGrid-cell:first-of-type': {
        borderTopLeftRadius: '12px',
    },
    '& .MuiDataGrid-row--firstVisible:hover .MuiDataGrid-cell:last-of-type': {
        borderTopRightRadius: '12px',
    },
    '& .MuiDataGrid-row--lastVisible:hover .MuiDataGrid-cell:first-of-type': {
        borderBottomLeftRadius: '12px',
    },
    '& .MuiDataGrid-row--lastVisible:hover .MuiDataGrid-cell:last-of-type': {
        borderBottomRightRadius: '12px',
    },
    '& .MuiDataGrid-row.Mui-selected': { bgcolor: 'rgba(255,201,101,0.08)' },
    '& .MuiDataGrid-row.Mui-selected:hover': { bgcolor: 'rgba(255,201,101,0.1)' },
    '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell': { bgcolor: 'rgba(255,201,101,0.1)' },
    '& .MuiDataGrid-filler': { bgcolor: midnight.surfaceHighest },
    '& .MuiDataGrid-columnSeparator': { color: 'rgba(255,255,255,0.06)' },
    '& .MuiDataGrid-footerContainer': { display: 'none' },
    '& .MuiDataGrid-sortIcon': { color: midnight.onSurfaceVariant },
    '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': { color: midnight.primary },
};
